// „Montant à payer“ (user decision 14.10.2026): what will be paid for an incoming invoice – TTC, after escompte
// (EscCalc), only the VAT or another amount. Shown in the import review (under Escompte) and in the edit dialog, in the
// look of the import design's fields (Barlow labels, 32 px boxes, amount in teal like „ZU ZAHLEN“).
import { useEffect, useState, type CSSProperties } from 'react';
import { PAY_MODES, type PayMode } from '@rimon/contracts';
import { formatEur, parseUserAmount, toCents } from '../../api/money';
import { tr2 } from '../../i18n/lang';
import { modeLabel } from '../../data/toPay';

const LABEL: CSSProperties = { font: '700 9px/1 Barlow,sans-serif', letterSpacing: '.14em', color: '#9B9698' };
const TEAL = '#05A79D';

export interface PayChoiceProps {
  mode: PayMode; amount?: string | null; chosen?: boolean;
  /** Escompte set on the invoice (the „Nach Escompte“ choice); `escNote` e.g. „2 % bis 26.10.2026“. */
  hasEscompte: boolean; escNote?: string;
  readOnly?: boolean;
  onMode: (mode: PayMode) => void;
  /** Only for CUSTOM: the typed amount as API decimal string. */
  onAmount: (amount: string | undefined) => void;
}

export function PayChoice({ mode, amount, chosen, hasEscompte, escNote, readOnly, onMode, onAmount }: PayChoiceProps) {
  const [typed, setTyped] = useState<string | null>(null);
  useEffect(() => { if (mode !== 'CUSTOM') setTyped(null); }, [mode]);
  const shown = amount ? formatEur(toCents(amount)) : '—';
  const hint = mode === 'ESCOMPTE' ? tr2('Nach Escompte', 'Après escompte') + (escNote ? ' · ' + escNote : '')
    : mode === 'TVA' ? tr2('Nur die TVA wird bezahlt', 'Seule la TVA est payée')
    : mode === 'CUSTOM' ? tr2('Eigener Betrag', 'Montant saisi')
    : hasEscompte ? tr2('Voller Betrag, ohne Escompte', 'Montant plein, sans escompte') : tr2('Rechnungsbetrag', 'Montant de la facture');
  return (
    <div data-pay-choice style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 6, border: '1px solid rgba(19,117,189,.45)', background: 'rgba(19,117,189,.07)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ font: '600 13px/1 Inter,sans-serif', flex: 1 }}>{tr2('Montant à payer', 'Montant à payer')}</div>
        <div style={{ font: '500 11px/1 Inter,sans-serif', color: chosen ? '#6DA8DC' : '#9B9698' }}>{chosen ? tr2('gewählt', 'choisi') : tr2('Vorschlag', 'suggestion')}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 6 }}>
        {PAY_MODES.map((m) => {
          const on = m === mode, off = m === 'ESCOMPTE' && !hasEscompte;
          return (
            <div key={m} role="button" title={off ? tr2('Kein Escompte auf dieser Rechnung', 'Pas d’escompte sur cette facture') : modeLabel(m)}
              onClick={() => { if (!readOnly && !off && !on) onMode(m); }}
              style={{ height: 32, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', textAlign: 'center',
                font: '600 11.5px/1.15 Inter,sans-serif', cursor: readOnly || off ? 'default' : 'pointer', opacity: off ? 0.35 : 1,
                background: on ? '#1375BD' : 'transparent', border: '1px solid ' + (on ? '#1375BD' : 'rgba(255,255,255,.16)'), color: '#FBFAFA' }}>
              {m === 'TTC' ? 'TTC' : m === 'ESCOMPTE' ? tr2('Nach Escompte', 'Après escompte') : m === 'TVA' ? tr2('Nur TVA', 'TVA seule') : tr2('Anderer', 'Autre')}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 0 }}>
          <div style={LABEL}>{tr2('ZU ZAHLEN', 'À PAYER')}</div>
          {mode === 'CUSTOM' && !readOnly
            ? <input autoComplete="off" value={typed ?? (amount ? formatEur(toCents(amount)).replace(/\s?€$/, '') : '')} placeholder="0,00"
                onChange={(e) => setTyped(e.target.value)}
                onBlur={() => { if (typed === null) return; const v = parseUserAmount(typed); onAmount(v); setTyped(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                style={{ height: 32, borderRadius: 4, border: '1px solid ' + (amount ? 'rgba(255,255,255,.14)' : 'rgba(224,163,64,.7)'), background: 'rgba(255,255,255,.03)', padding: '0 9px', color: TEAL, font: "600 15px/1 'JetBrains Mono',monospace", outline: 'none', minWidth: 0 }} />
            : <div style={{ height: 32, display: 'flex', alignItems: 'center', font: "600 15px/1 'JetBrains Mono',monospace", color: TEAL }}>{shown}</div>}
        </div>
        <div style={{ font: '500 10.5px/1.3 Inter,sans-serif', color: '#9B9698', paddingBottom: 9, textAlign: 'right', maxWidth: '55%' }}>{hint}</div>
      </div>
    </div>
  );
}
